import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  IconButton,
} from '@mui/material';
import { PictureAsPdf, Security, OpenInNew } from '@mui/icons-material';
import { TransporterGroup, Transporter, GroupInsuranceCertificate } from '@otw/models';
import { apiClient } from '@/core/api/apiClient';

interface EditGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (groupId: string, transporterIds: string[], adminIds: string[], pricePerMile?: number) => Promise<void>;
  group: TransporterGroup | null;
  transporters: Transporter[];
}

const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  open,
  onClose,
  onSave,
  group,
  transporters
}) => {
  const [selectedTransporters, setSelectedTransporters] = useState<Transporter[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<Transporter[]>([]);
  const [pricePerMile, setPricePerMile] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    transporters?: string;
    admins?: string;
    pricePerMile?: string;
  }>({});

  // Filter transporters by role
  const driverTransporters = transporters.filter(t => t.transporter_role === 'driver');
  const adminTransporters = transporters.filter(t => t.transporter_role === 'admin');
  const ownerTransporters = transporters.filter(t => t.transporter_role === 'owner');

  useEffect(() => {
    if (open && group) {
      // Initialize with current group members
      const groupDrivers = transporters.filter(t => 
        group.driver_ids.includes(t.id) && t.transporter_role === 'driver'
      );
      const groupAdmins = transporters.filter(t => 
        group.admin_ids.includes(t.id) && t.transporter_role === 'admin'
      );
      
      setSelectedTransporters(groupDrivers);
      setSelectedAdmins(groupAdmins);
      setPricePerMile(group.price_per_mile || '');
      setErrors({});
    }
  }, [open, group, transporters]);

  const resetForm = () => {
    setSelectedTransporters([]);
    setSelectedAdmins([]);
    setPricePerMile('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: { transporters?: string; admins?: string; pricePerMile?: string } = {};

    if (pricePerMile !== '' && (isNaN(Number(pricePerMile)) || Number(pricePerMile) < 0)) {
      newErrors.pricePerMile = 'Price per mile must be a valid positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!group || !validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSave(
        group.id, 
        selectedTransporters.map(t => t.id),
        selectedAdmins.map(t => t.id),
        pricePerMile !== '' ? Number(pricePerMile) : undefined
      );
      resetForm();
    } catch (error) {
      console.error('Error saving group:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const certificates = group?.insurance_certificates ?? [];
  const [loadingCertPath, setLoadingCertPath] = useState<string | null>(null);

  const handleViewCert = useCallback(
    async (cert: GroupInsuranceCertificate) => {
      if (!group || !cert.storagePath) return;
      setLoadingCertPath(cert.storagePath);
      try {
        const { url } = await apiClient.portal.admin.getGroupInsuranceCertificateUrl(group.id, cert.storagePath);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.error('Failed to open certificate:', err);
      } finally {
        setLoadingCertPath(null);
      }
    },
    [group]
  );

  const formatCertDate = (uploadedAt: GroupInsuranceCertificate['uploadedAt']) => {
    if (!uploadedAt) return '';
    const date = typeof uploadedAt.toDate === 'function' ? uploadedAt.toDate() : (uploadedAt as { seconds?: number })?.seconds ? new Date((uploadedAt as { seconds: number }).seconds * 1000) : null;
    return date ? date.toLocaleDateString() : '';
  };

  if (!group) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Company</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {group.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manage the transporters in this group. You can add or remove transporters but cannot change the company name or owner.
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2" gutterBottom>
                Owner:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const owner = transporters.find(t => t.id === group.owner_id);
                  return owner 
                    ? `${owner.first_name && owner.last_name 
                        ? `${owner.first_name} ${owner.last_name}` 
                        : owner.email} (${owner.email})`
                    : 'Owner not found';
                })()}
              </Typography>
            </Box>
          </Box>

          {/* Insurance certificates (uploaded in vehicle-uploader by owners/admins) */}
          {certificates.length > 0 && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Security fontSize="small" />
                Insurance certificates
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Uploaded by company owners/admins in the vehicle uploader dashboard.
              </Typography>
              <List dense disablePadding>
                {certificates.map((cert, idx) => (
                  <ListItem
                    key={cert.storagePath || idx}
                    sx={{ px: 0, py: 0.5 }}
                    secondaryAction={
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="View certificate"
                          onClick={() => handleViewCert(cert)}
                          disabled={!!loadingCertPath}
                        >
                          {loadingCertPath === cert.storagePath ? (
                            <CircularProgress size={20} />
                          ) : (
                            <OpenInNew fontSize="small" />
                          )}
                        </IconButton>
                      </ListItemSecondaryAction>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PictureAsPdf fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={cert.fileName}
                      secondary={[
                        cert.fileSize ? `${(cert.fileSize / 1024).toFixed(1)} KB` : null,
                        formatCertDate(cert.uploadedAt),
                      ].filter(Boolean).join(' • ')}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          <TextField
            label="Price per Mile (Optional)"
            type="number"
            fullWidth
            value={pricePerMile}
            onChange={(e) => setPricePerMile(e.target.value === '' ? '' : Number(e.target.value))}
            error={!!errors.pricePerMile}
            helperText={errors.pricePerMile || "Enter the price per mile for this company (e.g., 2.50)"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: 'text.secondary' }}>$</Typography>
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              step: 0.01,
              placeholder: "0.00"
            }}
          />
          
          <FormControl fullWidth error={!!errors.transporters}>
            <Autocomplete
              multiple
              id="drivers-select"
              options={driverTransporters}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={selectedTransporters}
              onChange={(_, newValue) => setSelectedTransporters(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  label="Select Drivers"
                  error={!!errors.transporters}
                  helperText={errors.transporters || "Add or remove drivers from this group (optional)"}
                  placeholder={selectedTransporters.length === 0 ? 'Select drivers...' : `${selectedTransporters.length} drivers selected`}
                />
              )}
            />
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth error={!!errors.admins}>
            <Autocomplete
              multiple
              id="admins-select"
              options={adminTransporters}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={selectedAdmins}
              onChange={(_, newValue) => setSelectedAdmins(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  label="Select Admins"
                  error={!!errors.admins}
                  helperText={errors.admins || "Add or remove admins from this group"}
                  placeholder={selectedAdmins.length === 0 ? 'Select admins...' : `${selectedAdmins.length} admins selected`}
                />
              )}
            />
          </FormControl>
          
  
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGroupDialog;
